import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BottomDiv, Heading, List, Status, Task, TaskHeading } from './styles';
import { isEmpty, renderStatusIconName } from '../../helpers/common';
import { updateTaskDetails } from '../../services/taskServices';
import { DropdownButton } from '../dropdown';
import { ProjectModel } from '../../mobx/models/project';
import { TaskModel } from '../../mobx/models/task';
import { DEBOUNCE_DELAYS, STATUS_TYPE } from '../../global/constants';
import { observer } from 'mobx-react-lite';
import { rootStore } from '../../mobx/rootStore';
import ModalHeader from '../modals/modalHeader';
import debounce from 'lodash/debounce';

interface Props {
  onClose?: () => void;
  currentProject?: ProjectModel;
  currentTask?: TaskModel;
}

const SubTaskPopup = (props: Props) => {
  const { onClose, currentProject, currentTask } = props;
  //State
  const [searchText, setSearchText] = useState('');
  const [debouncedSearchText, setDebouncedSearchText] = useState('');

  // Update the search value only after the user stops typing, so filtering doesn't run on every key.
  const debouncedSetSearch = useRef(
    debounce((value: string) => setDebouncedSearchText(value), DEBOUNCE_DELAYS.SEARCH)
  ).current;

  // Stop any pending update when the popup closes.
  useEffect(() => () => debouncedSetSearch.cancel(), [debouncedSetSearch]);

  // Build the list of tasks that can become sub-tasks. Recomputes only when the tasks change.
  const filteredSubTaskList = useMemo<TaskModel[]>(() => {
    const projectTasks = currentProject?.tasks || [];
    const uncompletedTask = projectTasks?.filter(
      (task: TaskModel) => !task.status && task.subTasks.length === 0 && task.id !== currentTask?.id && !task.parentTask
    );
    return (
      uncompletedTask?.toSorted((a: TaskModel, b: TaskModel) => {
        const dateA = new Date(a?.createdOn).getTime();
        const dateB = new Date(b?.createdOn).getTime();
        return dateB - dateA;
      }) || []
    );
  }, [currentProject?.tasks, currentTask?.id]);

  const handleChange = useCallback(
    (value: string) => {
      setSearchText(value);
      debouncedSetSearch(value);
    },
    [debouncedSetSearch]
  );

  const searchedList = useMemo(() => {
    const searchNewText = debouncedSearchText?.trim().toLowerCase();
    const words = searchNewText?.split(' ').filter((word) => word.length > 0);
    return (
      filteredSubTaskList?.filter((item: TaskModel) => {
        const title = item?.name.trim().toLowerCase() || '';
        return words.some((word) => title.includes(word));
      }) || []
    );
  }, [filteredSubTaskList, debouncedSearchText]);

  const handleTaskClick = useCallback(
    async (taskId: string, projectId: string) => {
      try {
        const payload = {
          parentTaskId: currentTask?.id,
          projectId: projectId
        };
        await updateTaskDetails(taskId, payload);
        rootStore.taskStore.tasks.find((x) => x.id === taskId)?.setParentTask(currentTask?.id || '');
        if (onClose) onClose();
        // loadParentTaskData();
      } catch (error) {
        console.log('error: ', error);
      }
    },
    [currentTask?.id, onClose]
  );

  const renderSubtaskList = useMemo(() => {
    if (isEmpty(filteredSubTaskList)) return null;
    const defaultStatus = currentProject?.statusData?.find((x) => x.Default);
    const updatedList = !isEmpty(debouncedSearchText) ? searchedList : filteredSubTaskList;
    return updatedList?.map((item: TaskModel) => {
      const currentStatus =
        currentProject?.statusData?.find((x) => x['_id'] === item.statusId) ||
        currentProject?.defaultStatus ||
        currentProject?.statusData?.[0];
      return (
        <Task role='button' key={item?.id} onClick={() => handleTaskClick(item?.id, item?.projectId)}>
          <Status>
            <DropdownButton
              iconName={renderStatusIconName(currentStatus?.Type || defaultStatus?.Type || STATUS_TYPE.BACKLOG)}
              variant={'no-border'}
            />
          </Status>
          <TaskHeading>{item?.name}</TaskHeading>
        </Task>
      );
    });
  }, [
    currentProject?.defaultStatus,
    currentProject?.statusData,
    filteredSubTaskList,
    handleTaskClick,
    searchedList,
    debouncedSearchText
  ]);

  return (
    <>
      <ModalHeader
        placeHolder='Search for task to add as a sub-task...'
        autoFocus={true}
        searchValue={searchText}
        onSearchChange={handleChange}
        onClose={onClose}
      />
      <BottomDiv>
        <Heading>Recent</Heading>
        <List>{renderSubtaskList}</List>
      </BottomDiv>
    </>
  );
};

export default observer(SubTaskPopup);
